import React, { useEffect, useState, useRef, useCallback } from "react";
import "../styles/AdminShippingCharges.css";
import { SHIPPING_CHARGES_API } from "../config/api";

function AdminShippingCharges() {
  const [expressZones, setExpressZones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [zoneEditForm, setZoneEditForm] = useState({
    rate_250: "",
    rate_500: "",
    rate_add_500: "",
  });

  const popupRef = useRef(null);

  /* ================= SHOW POPUP ALERT ================= */
  const showPopup = useCallback((message, type = "success") => {
    if (popupRef.current) {
      popupRef.current.remove();
    }

    const popup = document.createElement("div");
    popup.className = `admin-popup ${type}`;
    popup.innerHTML = `<div class="popup-content">${message}</div>`;
    popupRef.current = popup;

    document.body.appendChild(popup);

    setTimeout(() => {
      popup.classList.add("hide");
    }, 2200);

    setTimeout(() => {
      popup.remove();
      popupRef.current = null;
    }, 2800);
  }, []);

  /* ================= FETCH EXPRESS ZONES ================= */
  const fetchExpressZones = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${SHIPPING_CHARGES_API}/express_zones`);
      const data = await res.json();
      if (data.status === "success" && Array.isArray(data.zones)) {
        setExpressZones(data.zones);
      } else {
        setExpressZones([]);
      }
    } catch (err) {
      console.error("Fetch express zones error:", err);
      showPopup("Failed to load express shipping rates matrix", "error");
    } finally {
      setLoading(false);
    }
  }, [showPopup]);

  useEffect(() => {
    fetchExpressZones();
  }, [fetchExpressZones]);

  /* ================= EDIT ZONE ================= */
  const startEditZone = (z) => {
    setEditingZone(z.zone_code);
    setZoneEditForm({
      rate_250: z.rate_250,
      rate_500: z.rate_500,
      rate_add_500: z.rate_add_500,
    });
  };

  const handleUpdateZone = async (zone_code) => {
    const parsed250 = parseFloat(zoneEditForm.rate_250);
    const parsed500 = parseFloat(zoneEditForm.rate_500);
    const parsedAdd500 = parseFloat(zoneEditForm.rate_add_500);

    if (isNaN(parsed250) || parsed250 < 0 || isNaN(parsed500) || parsed500 < 0 || isNaN(parsedAdd500) || parsedAdd500 < 0) {
      return showPopup("Please enter valid positive rate values", "error");
    }

    try {
      setLoading(true);
      const res = await fetch(`${SHIPPING_CHARGES_API}/express_zones/${zone_code}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          rate_250: parsed250,
          rate_500: parsed500,
          rate_add_500: parsedAdd500,
        }),
      });

      const result = await res.json();

      if (res.ok && result.status === "success") {
        showPopup("Express zone rate updated successfully");
        setEditingZone(null);
        fetchExpressZones();
      } else {
        showPopup(result.message || "Failed to update zone rates", "error");
      }
    } catch (err) {
      console.error("Update zone error:", err);
      showPopup("Server error occurred", "error");
    } finally {
      setLoading(false);
    }
  };

  const cancelEdit = () => {
    setEditingZone(null);
  };

  // Helper for cleaning zone display names
  const cleanZoneName = (name) => {
    return name.replace(" (Rest of India)", "");
  };

  return (
    <div className="admin-container shipping-charges-admin-container">
      {/* ================= HEADER ================= */}
      <div className="shipping-dashboard-header">
        <div className="header-title-area">
          <h1>
            <i className="bi bi-truck-flatbed"></i> Express Shipping Charges Matrix
          </h1>
          <p>Configure express delivery fees dynamically based on destination geographical zones and package weight brackets.</p>
        </div>
      </div>

      {/* ================= STATS ================= */}
      <div className="shipping-metrics-grid">
        <div className="metric-card">
          <div className="metric-icon zones"><i className="bi bi-geo-alt-fill"></i></div>
          <div className="metric-info">
            <span className="metric-value">{expressZones.length} Express Zones</span>
            <span className="metric-label">Active Shipping Coverage</span>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon lowest"><i className="bi bi-arrow-down-circle-fill"></i></div>
          <div className="metric-info">
            <span className="metric-value">
              ₹{expressZones.length > 0 ? Math.min(...expressZones.map((z) => parseFloat(z.rate_250) || 0)).toFixed(2) : "50.00"}
            </span>
            <span className="metric-label">Min Base Rate (Upto 250g)</span>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon highest"><i className="bi bi-arrow-up-circle-fill"></i></div>
          <div className="metric-info">
            <span className="metric-value">
              ₹{expressZones.length > 0 ? Math.max(...expressZones.map((z) => parseFloat(z.rate_500) || 0)).toFixed(2) : "125.00"}
            </span>
            <span className="metric-label">Max Base Rate (Upto 500g)</span>
          </div>
        </div>
      </div>

      {/* ================= MAIN MATRIX TABLE CARD ================= */}
      <div className="admin-section list-card">
        <div className="list-header-deck">
          <h2>
            <i className="bi bi-table"></i> Configured Express Rate Matrix
          </h2>
          <span className="count-badge">
            {expressZones.length} ZONES ACTIVE
          </span>
        </div>
        <p>Live courier matrix applied at checkout. Rates are calculated automatically by matching the customer's shipping address state/city and weight in grams.</p>

        {loading && expressZones.length === 0 ? (
          <div className="shipping-loading-spinner">
            <div className="spinner"></div>
            <p>Syncing express rate matrix...</p>
          </div>
        ) : (
          <div className="shipping-table-wrapper">
            <table className="shipping-admin-table express-matrix-grid-table">
              <thead>
                <tr>
                  <th rowSpan="2" className="th-zone-col">ZONE</th>
                  <th colSpan="3" className="th-express-header">EXPRESS RATE</th>
                  <th rowSpan="2" className="th-actions-col">ACTIONS</th>
                </tr>
                <tr>
                  <th className="th-sub-col">Upto 250Gms</th>
                  <th className="th-sub-col">Upto 500Gms</th>
                  <th className="th-sub-col">Every 0.500 Gms upto 5Kg</th>
                </tr>
              </thead>
              <tbody>
                {expressZones.map((z) => (
                  <tr key={z.zone_code} className={editingZone === z.zone_code ? "row-editing-active" : ""}>
                    <td className="zone-name-cell">
                      <strong>{cleanZoneName(z.zone_name)}</strong>
                    </td>

                    <td className="rate-cell">
                      {editingZone === z.zone_code ? (
                        <div className="input-with-symbol">
                          <span>₹</span>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={zoneEditForm.rate_250}
                            onChange={(e) => setZoneEditForm({ ...zoneEditForm, rate_250: e.target.value })}
                            className="edit-inner-input"
                          />
                        </div>
                      ) : (
                        <span className="rate-val-bold">{parseInt(parseFloat(z.rate_250))}</span>
                      )}
                    </td>

                    <td className="rate-cell">
                      {editingZone === z.zone_code ? (
                        <div className="input-with-symbol">
                          <span>₹</span>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={zoneEditForm.rate_500}
                            onChange={(e) => setZoneEditForm({ ...zoneEditForm, rate_500: e.target.value })}
                            className="edit-inner-input"
                          />
                        </div>
                      ) : (
                        <span className="rate-val-bold">{parseInt(parseFloat(z.rate_500))}</span>
                      )}
                    </td>

                    <td className="rate-cell">
                      {editingZone === z.zone_code ? (
                        <div className="input-with-symbol">
                          <span>₹</span>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={zoneEditForm.rate_add_500}
                            onChange={(e) => setZoneEditForm({ ...zoneEditForm, rate_add_500: e.target.value })}
                            className="edit-inner-input"
                          />
                        </div>
                      ) : (
                        <span className="rate-val-bold">{parseInt(parseFloat(z.rate_add_500))}</span>
                      )}
                    </td>

                    <td className="actions-cell">
                      {editingZone === z.zone_code ? (
                        <div className="action-buttons-inline">
                          <button
                            type="button"
                            className="btn-action-save"
                            onClick={() => handleUpdateZone(z.zone_code)}
                            disabled={loading}
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            className="btn-action-cancel"
                            onClick={cancelEdit}
                            disabled={loading}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="btn-action-edit"
                          onClick={() => startEditZone(z)}
                          title="Edit Zone Rates"
                        >
                          <i className="bi bi-pencil-fill"></i>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ================= CALCULATION GUIDELINES ================= */}
        <div className="guidelines-box mt-4" style={{ marginTop: "24px" }}>
          <h4>
            <i className="bi bi-info-circle-fill"></i> Weight Calculation Rules:
          </h4>
          <ul style={{ margin: "0", paddingLeft: "20px", fontSize: "13.5px", color: "#514125", lineHeight: "1.7" }}>
            <li><strong>Upto 250Gms</strong>: Applicable when total package weight is 250g or below.</li>
            <li><strong>Upto 500Gms</strong>: Applicable when total package weight is between 251g and 500g.</li>
            <li><strong>Every 0.500 Gms upto 5Kg</strong>: For items exceeding 500g, the base rate (Upto 500Gms) is charged plus the additional fee for every additional 500g block.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default AdminShippingCharges;
